import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { licenseService } from "../Services/license.service";
import { useTheme as useBaseTheme } from "./ThemeContext";
import Cookies from "js-cookie";

const ThemeContext = createContext();

const FIXED_DOMAIN = process.env.REACT_APP_FIXED_DOMAIN || "";

// Logo default inline (base64 1px transparente) — sem import de asset
const DEFAULT_LOGO = `${process.env.PUBLIC_URL}/images/logo_ofv2.jpg`;

function currentHostname() {
  if (typeof window === "undefined") return "default";
  const h = window.location.hostname;
  if (h === "localhost" || h === "127.0.0.1") {
    try { return FIXED_DOMAIN ? new URL(FIXED_DOMAIN).hostname : "localhost"; } catch { return "localhost"; }
  }
  return h;
}

const COMPANY_COOKIE_KEY = () => `movyx-companyId-${currentHostname()}`;

const toKebab = (s = "") =>
  s
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/_/g, "-")
    .replace(/([a-z])([0-9]+)/gi, "$1-$2")
    .toLowerCase();

const flatten = (obj, parent = "") => {
  const res = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    const name = parent ? `${parent}-${toKebab(k)}` : toKebab(k);
    if (v && typeof v === "object" && !Array.isArray(v)) {
      Object.assign(res, flatten(v, name));
    } else {
      res[name] = v;
    }
  });
  return res;
};

const isAbsoluteUrl = (url) => typeof url === "string" && /^https?:\/\//.test(url);

const cleanString = (val) => {
  if (val === null || val === undefined || String(val).toLowerCase() === "null" || String(val).trim() === "") {
    return null;
  }
  return val;
};

export const NewThemeProvider = ({ children, companyId: propCompanyId }) => {
  const baseTheme = useBaseTheme();
  const isDark = baseTheme ? baseTheme.isDark : false;
  const [themeColors, setThemeColors] = useState({});
  const [platformName, setPlatformName] = useState(null);
  const [centralPhone, setCentralPhone] = useState(null);
  const [companyPhone, setCompanyPhone] = useState(null);
  const [phrase, setPhrase] = useState(null);
  const [androidLink, setAndroidLink] = useState(null);
  const [iosLink, setIosLink] = useState(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyMainImageUrl, setCompanyMainImageUrl] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [condensedMenu, setCondensedMenu] = useState(DEFAULT_LOGO);

  const lightColorsRef = useRef({});
  const darkColorsRef = useRef({});

  useEffect(() => {
    const colors = isDark ? darkColorsRef.current : lightColorsRef.current;
    if (Object.keys(colors).length > 0) {
      applyColorsToCSS(colors);
      setThemeColors(colors);
    }
  }, [isDark]);

  useEffect(() => {
    async function fetchAndApplyTheme() {
      let logoSet = false;
      try {
        let companyId = propCompanyId;

        if (!companyId) {
          const savedCompanyId = Cookies.get(COMPANY_COOKIE_KEY());
          if (savedCompanyId) companyId = savedCompanyId;
        }

        if (!companyId) {
          let domain = window.location.href;
          if (domain.endsWith("#/")) domain = domain.replace(/#\/$/, "");
          if (domain.includes("localhost") || domain.includes("127.0.0.1")) {
            domain = FIXED_DOMAIN;
          }
          const licensesByDomain = await licenseService.fetchLicensesByDomain(domain);
          const licenseByDomain =
            Array.isArray(licensesByDomain) && licensesByDomain.length > 0
              ? licensesByDomain[0]
              : null;
          if (licenseByDomain && licenseByDomain.licensedId) {
            companyId = licenseByDomain.licensedId;
            Cookies.set(COMPANY_COOKIE_KEY(), companyId, { expires: 30 });
          }
        }

        if (!companyId) return;

        const licenses = await licenseService.fetchLicenses(companyId);
        const license =
          Array.isArray(licenses) && licenses.length > 0 ? licenses[0] : null;

        // DEBUG — remove depois de confirmar
        console.log("[NewThemeContext] license completo:", license);
        console.log("[NewThemeContext] license.companyLogo:", license?.companyLogo);
        console.log("[NewThemeContext] license.condensedMenu:", license?.condensedMenu);

        if (!license || !license.colors) return;

        let colorsObj = license.colors;
        if (typeof colorsObj === "string") {
          colorsObj = colorsObj.replace(/\\/g, "").trim();
          const firstBrace = colorsObj.indexOf("{");
          const lastBrace = colorsObj.lastIndexOf("}");
          if (firstBrace !== -1 && lastBrace !== -1) {
            colorsObj = colorsObj.substring(firstBrace, lastBrace + 1);
          }
          try {
            colorsObj = JSON.parse(colorsObj);
          } catch (e) {
            console.error("Erro ao converter colors para objeto:", e);
            return;
          }
        }

        if (!colorsObj || (!colorsObj.light_theme && !colorsObj.dark_theme)) {
          console.error("colorsObj não possui light_theme ou dark_theme");
          return;
        }

        if (colorsObj.light_theme) lightColorsRef.current = flatten(colorsObj.light_theme);
        if (colorsObj.dark_theme) darkColorsRef.current = flatten(colorsObj.dark_theme);

        const selected = isDark ? darkColorsRef.current : lightColorsRef.current;
        if (!selected || Object.keys(selected).length === 0) {
          console.error("Tema selecionado não existe");
          return;
        }

        setThemeColors(selected);
        applyColorsToCSS(selected);

        // Logo principal
        const rawLogo = license.companyLogo || null;
        const resolvedLogo = isAbsoluteUrl(rawLogo) ? rawLogo : DEFAULT_LOGO;
        console.log("[NewThemeContext] resolvedLogo:", resolvedLogo);
        setCompanyLogo(resolvedLogo);
        logoSet = true;

        // Logo condensada
        const rawCondensedMenu = license.condensedMenu || null;
        const resolvedCondensedMenu = isAbsoluteUrl(rawCondensedMenu)
          ? rawCondensedMenu
          : DEFAULT_LOGO;
        setCondensedMenu(resolvedCondensedMenu);

        // Imagem de fundo do login
        const rawMainImage = license.homeScreenImage || null;
        setCompanyMainImageUrl(isAbsoluteUrl(rawMainImage) ? rawMainImage : null);

        setPlatformName(cleanString(license.platformName) || "Movyx");
        setCentralPhone(cleanString(license.centralPhone));
        setCompanyPhone(cleanString(license.companyPhone));
        setPhrase(cleanString(license.phrase));
        setAndroidLink(cleanString(license.androidLink));
        setIosLink(cleanString(license.iosLink));
        setFavicon(cleanString(license.favicon));
      } finally {
        if (!logoSet) setCompanyLogo(DEFAULT_LOGO);
        setLogoLoading(false);
      }
    }

    fetchAndApplyTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propCompanyId]);

  const applyColorsToCSS = (colors) => {
    try {
      const root = document.documentElement;
      Object.keys(colors).forEach((key) => {
        const value = colors[key];
        if (value !== undefined && value !== null) {
          root.style.setProperty(`--${key}`, value);
        }
      });
    } catch (err) {
      // fail silently
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        themeColors,
        logoLoading,
        companyLogo,
        companyMainImageUrl,
        platformName,
        centralPhone,
        companyPhone,
        phrase,
        androidLink,
        iosLink,
        favicon,
        condensedMenu,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
